/**
 * The learner's onboarding state on the account (owner 2026-10-12: "make onboarding journey etc persistent too at account
 * level in database instead of just in browser"; Sol onboarding-account-state D1-D6 all A, dev-pr1-check Q3 B).
 * GET/PATCH /api/user/onboarding: where the learner is in the guided first visit (moving forward only, by the step's
 * place in this journey; a replay is the one way back) and which one-time videos they have seen. The account is the
 * truth; this browser's old values (progress.js, the video keys) are copied up once as the player's import and stay as
 * a cache for when the server cannot be reached.
 */
import { QUIKLEARN_BACKEND_URL, API_KEY } from 'config/app.config';
import { STEPS } from './journey';

export const TUTORIAL_VIDEO = 'player_tutorial';
export const DONE_ORDER = 1000;
const STEP_SHAPE = /^[a-z][a-z0-9_]{1,39}$/;

/** A step's place in this journey ("done" last), the order the server keeps it moving forward by. */
export function stepOrder(stepId) {
  if (stepId === 'done') return DONE_ORDER;
  const index = STEPS.findIndex(step => step.id === stepId);
  return index >= 0 ? index : null;
}

/** What this browser held before the account kept it: sent once as the player's import (server values win). */
export function playerImport({ progress, tutorialSeen }) {
  const patch = { import: 'player' };
  const order = progress?.step ? stepOrder(progress.step) : null;
  if (order !== null && STEP_SHAPE.test(progress.step)) {
    patch.tourStep = progress.step;
    patch.tourStepOrder = order;
  }
  if (tutorialSeen) patch.tutorialVideosSeen = [TUTORIAL_VIDEO];
  return patch;
}

/** The PATCH for a step the tour reached; a replay start is sent as the explicit way back. */
export function stepPatch(stepId, { replayStart = false } = {}) {
  const order = stepOrder(stepId);
  if (order === null || !STEP_SHAPE.test(stepId)) return null;
  return replayStart ? { tourStep: stepId, tourStepOrder: order, tourReplay: true } : { tourStep: stepId, tourStepOrder: order };
}

let state = { token: null, view: null };
const baseUrl = () => String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
const headers = token => {
  const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  if (API_KEY && String(API_KEY).toLowerCase() !== 'dummy') h['x-api-key'] = API_KEY;
  return h;
};
async function request(token, method, body) {
  const response = await fetch(`${baseUrl()}/api/user/onboarding`, {
    method,
    headers: headers(token),
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error(`onboarding ${method} ${response.status}`);
  const payload = await response.json().catch(() => null);
  return payload?.data || null;
}

/**
 * Loads the account's state (and, on this browser's first load, imports its old values once). Resolves the view, or
 * null when the server cannot be reached - the caller then keeps this browser's values, as before.
 */
export async function loadAccountState({ token, local }) {
  if (!token) return null;
  state.token = token;
  try {
    let view = await request(token, 'GET');
    if (view && !view.playerImported) view = (await request(token, 'PATCH', playerImport(local || {}))) || view;
    state.view = view;
    return view;
  } catch (_) {
    return null;
  }
}

const patch = body => {
  if (!state.token || !body) return Promise.resolve(null);
  return request(state.token, 'PATCH', body)
    .then(view => {
      if (view) state.view = view;
      return view;
    })
    .catch(() => null);
};

export const saveStep = (stepId, options) => patch(stepPatch(stepId, options));
export const accountView = () => state.view;
export const accountSawVideo = key => Boolean(state.view?.tutorialVideosSeen?.includes(key));
export const markVideoSeen = key => {
  if (state.view && !accountSawVideo(key)) state.view = { ...state.view, tutorialVideosSeen: [...state.view.tutorialVideosSeen, key] };
  return patch({ tutorialVideosSeen: [key] });
};
